import * as THREE from 'three'
import { FullScreenQuad } from 'three/addons/postprocessing/Pass.js'
import velocityVertex from '../shaders/velocity.vert'
import velocityFragment from '../shaders/velocity.frag'
import screenVertex from '../shaders/quad.vert'
import compositeFragment from '../shaders/motionBlur.frag'

/**
 * Per-object motion blur, after gkjohnson's MotionBlurPass (John Chapman's approach):
 *   1. the scene renders to a colour buffer as normal
 *   2. every mesh is drawn again into a velocity buffer, with each pixel holding how far
 *      it moved on screen since the last frame (each mesh keeps a velocity material and
 *      its last-frame transform)
 *   3. a full-screen pass smears the colour buffer along each pixel's velocity
 *
 * The colour buffer is linear half-float; the composite converts to sRGB for the screen.
 *
 * @param {number} shutter  seconds of movement the smear covers. Velocity is scaled by
 *                          shutter / frame time, so the blur looks the same at 60 and 120Hz
 *                          and a dropped frame doesn't streak. 1/60 = one full 60fps frame.
 * @param {number} samples  taps per pixel along the velocity
 * @param {number} maxBlur  longest smear in CSS pixels
 */
export default class MotionBlur {
	constructor(renderer, { shutter = 1 / 60, samples = 16, maxBlur = 128 } = {}) {
		this.renderer = renderer
		this.shutter = shutter
		this.maxBlur = maxBlur

		const options = { type: THREE.HalfFloatType, depthBuffer: true }
		this.colourTarget = new THREE.WebGLRenderTarget(1, 1, options)
		this.velocityTarget = new THREE.WebGLRenderTarget(1, 1, {
			...options,
			minFilter: THREE.NearestFilter,
			magFilter: THREE.NearestFilter,
		})

		this.composite = new FullScreenQuad(
			new THREE.ShaderMaterial({
				vertexShader: screenVertex,
				fragmentShader: compositeFragment,
				defines: { SAMPLES: samples },
				uniforms: {
					uColour: { value: this.colourTarget.texture },
					uVelocity: { value: this.velocityTarget.texture },
					uResolution: { value: new THREE.Vector2() },
					uScale: { value: 1 },
					uMaxBlur: { value: maxBlur },
				},
				depthTest: false,
				depthWrite: false,
			})
		)

		// last-frame state: the view matrix, and { material, matrixWorld } per mesh
		// (the projection only changes on resize, which starts the history over)
		this.prevViewMatrix = new THREE.Matrix4()
		this.meshes = new Map()
		this.stale = true // last-frame state doesn't apply (first frame, or after a resize)

		this.resize()
	}

	/** Match the buffers to the drawing buffer. Everything moves on a resize, so the next frame isn't blurred. */
	resize() {
		const size = this.renderer.getDrawingBufferSize(new THREE.Vector2())
		this.colourTarget.setSize(size.x, size.y)
		this.velocityTarget.setSize(size.x, size.y)
		this.composite.material.uniforms.uResolution.value.copy(size)
		this.composite.material.uniforms.uMaxBlur.value = this.maxBlur * this.renderer.getPixelRatio()
		this.stale = true
	}

	/**
	 * @param {Iterable<THREE.Object3D>} [jumped]  objects that were moved rather than animated since
	 *   the last frame (a record snapping to a new spot): their last-frame transform is replaced
	 *   with this frame's, so the jump isn't smeared across the screen
	 */
	render(scene, camera, delta, jumped = []) {
		const { renderer } = this

		renderer.setRenderTarget(this.colourTarget)
		renderer.render(scene, camera) // updates every matrixWorld + the camera for this frame

		if (this.stale) this.#remember(scene, camera)
		for (const object of jumped) this.#forget(object)
		this.#renderVelocity(scene, camera)
		this.#remember(scene, camera)

		this.composite.material.uniforms.uScale.value = Math.min(this.shutter / Math.max(delta, 1e-3), 4)
		renderer.setRenderTarget(null)
		this.composite.render(renderer)
	}

	/** Draw the scene with every mesh wearing its velocity material, then put the real ones back. */
	#renderVelocity(scene, camera) {
		const swapped = []
		scene.traverseVisible(object => {
			if (!object.isMesh) return
			const state = this.#stateFor(object)
			const { uPrevModelViewMatrix } = state.material.uniforms
			uPrevModelViewMatrix.value.multiplyMatrices(this.prevViewMatrix, state.matrixWorld)
			swapped.push([object, object.material])
			object.material = state.material
		})

		this.renderer.setRenderTarget(this.velocityTarget)
		this.renderer.render(scene, camera)
		swapped.forEach(([object, material]) => (object.material = material))
	}

	/** Save this frame's transforms for the next one - hidden meshes too, so they don't jump when shown. */
	#remember(scene, camera) {
		this.prevViewMatrix.copy(camera.matrixWorldInverse)
		scene.traverse(object => {
			if (object.isMesh) this.#stateFor(object).matrixWorld.copy(object.matrixWorld)
		})
		this.stale = false
	}

	/** Treat `object` (and everything inside it) as having been where it is now last frame too. */
	#forget(object) {
		object.traverse(child => {
			if (child.isMesh) this.#stateFor(child).matrixWorld.copy(child.matrixWorld)
		})
	}

	#stateFor(mesh) {
		let state = this.meshes.get(mesh)
		if (!state) {
			state = {
				matrixWorld: mesh.matrixWorld.clone(),
				material: new THREE.ShaderMaterial({
					vertexShader: velocityVertex,
					fragmentShader: velocityFragment,
					uniforms: { uPrevModelViewMatrix: { value: new THREE.Matrix4() } },
					side: mesh.material.side, // the disc is seen from behind mid-flip
				}),
			}
			this.meshes.set(mesh, state)
		}
		return state
	}
}
